// Built-ins
import { useState, useEffect } from "react";

// Icons / Images
import { BiPlus } from "react-icons/bi";
import { AiOutlineWarning, AiOutlineCheckCircle } from "react-icons/ai";

import axios from "axios";

// Modules / Functions
import Swal from "sweetalert2";
import { Modal, Col, Form, Row } from "react-bootstrap";
import GuardedModal from "../../common/GuardedModal";
import { Button } from "semantic-ui-react";

// Global Variables
import { headers } from "../../global_vars";
import SelectWithInlineCreate from "../../common/SelectWithInlineCreate";
import AddBankModal from "./add_bank";
import AddClientModal from "./add_client";

// Variables
window.Swal = Swal;

// API endpoint
const ADD_BANK_CLIENT_ACCOUNT = "https://ultima.icsgr.com/api/data_management/bank_client_accounts/";

function AddBankClientAccountModal({ refreshData, defaultClientId, lockClient = false, defaultBankId, lockBank = false }) {
  const [show, setShow] = useState(false);
  const [addBankOpen, setAddBankOpen] = useState(false);
  const [addClientOpen, setAddClientOpen] = useState(false);
  
  // Form state
  const [client_id, setClient_id] = useState("");
  const [bank_id, setBank_id] = useState("");
  const [transitnumber, setTransitnumber] = useState("");
  const [accountnumber, setAccountnumber] = useState("");
  const [iban, setIban] = useState("");
  const [active, setActive] = useState(true);
  
  // Dropdown data
  const [clients, setClients] = useState([]);
  const [banks, setBanks] = useState([]);

  const resetForm = () => {
    setClient_id("");
    setBank_id("");
    setTransitnumber("");
    setAccountnumber("");
    setIban("");
    setActive(true);
  };

  const handleClose = () => setShow(false);
  const handleShow = () => {
    resetForm();
    if (defaultClientId) setClient_id(defaultClientId);
    if (defaultBankId) setBank_id(defaultBankId);
    setShow(true);
  };

  // Load dropdown data when modal opens
  useEffect(() => {
    if (show) {
      loadDropdownData();
    }
  }, [show]);

  const loadDropdownData = async () => {
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken"),
      };
      // Fetch clients
      const clientsResponse = await axios.get(
        "https://ultima.icsgr.com/api/data_management/all_clients/",
        { headers: currentHeaders }
      );
      setClients(clientsResponse.data.all_clients || []);

      // Fetch banks
      const banksResponse = await axios.get(
        "https://ultima.icsgr.com/api/administration/all_banks/",
        { headers: currentHeaders }
      );
      setBanks(banksResponse.data.all_banks || []);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  // Validation
  const isClientValid = client_id !== "";
  const isBankValid = bank_id !== "";
  const isTransitnumberValid = transitnumber.trim().length === 5 && /^\d{5}$/.test(transitnumber.trim());
  const isAccountnumberValid = accountnumber.trim().length >= 2 && accountnumber.trim().length <= 20;

  const isFormValid = isClientValid && isBankValid && isTransitnumberValid && isAccountnumberValid;

  const createNewBankClientAccount = async () => {
    if (!isFormValid) {
      return;
    }

    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken"),
      };
      await axios.post(
        ADD_BANK_CLIENT_ACCOUNT,
        {
          client_id,
          bank_id,
          transitnumber: transitnumber.trim(),
          accountnumber: accountnumber.trim(),
          iban: iban.trim() || null,
          active
        },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Bank Client Account created successfully", "success");
      if (refreshData) refreshData();
      handleClose();
    } catch (error) {
      console.error("Error creating bank client account:", error);
      const errorMessage = error.response?.data?.error || "Failed to create bank client account";
      Swal.fire("Error", errorMessage, "error");
    }
  };

  return (
    <>
      <Button color="green" style={{ margin: 20 }} onClick={handleShow}>
        <BiPlus style={{ color: "white", fontSize: "1.3em", marginRight: "0.3em" }}/>
        Create new Bank Client Account
      </Button>

      <GuardedModal
        show={show}
        onHide={handleClose}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Create new Bank Client Account
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="justify-content-md-center">
            <Col>
              <Form>
                {/* Basic Information */}
                <h6 className="mb-3">Basic Information</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">

                      <Form.Label>Account number *:</Form.Label>
                      <Form.Control
                        maxLength={20}
                        placeholder="e.g., 1234567890"
                        onChange={(e) => setAccountnumber(e.target.value)}
                        value={accountnumber}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Account Information */}
                <h6 className="mb-3 mt-4">Account Information</h6>
                <Row>
                  <Col md={6}>
                    <SelectWithInlineCreate
                      className="mb-3"
                      label="Client"
                      required
                      value={client_id}
                      onChange={setClient_id}
                      disabled={lockClient}
                      placeholderOption="Select Client"
                      onAddClick={lockClient ? undefined : () => setAddClientOpen(true)}
                      overviewLinkEntity="client"
                    >
                      {Array.isArray(clients) && clients.map((client) => (
                        <option key={client.client_id} value={client.client_id} title={client.client_id}>
                          {client.fullname || `${client.surname} ${client.name}`.trim() || client.client_id}
                        </option>
                      ))}
                    </SelectWithInlineCreate>
                  </Col>
                  <Col md={6}>
                    <SelectWithInlineCreate
                      className="mb-3"
                      label="Bank"
                      required
                      value={bank_id}
                      onChange={setBank_id}
                      disabled={lockBank}
                      placeholderOption="Select Bank"
                      onAddClick={lockBank ? undefined : () => setAddBankOpen(true)}
                      overviewLinkEntity="bank"
                    >
                      {Array.isArray(banks) && banks.map((bank) => (
                        <option key={bank.bank_id} value={bank.bank_id} title={bank.bank_id}>
                          {bank.bankname}
                        </option>
                      ))}
                    </SelectWithInlineCreate>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Branch number:</Form.Label>
                      <Form.Control
                        maxLength={5}
                        placeholder="e.g., 12345"
                        onChange={(e) => setTransitnumber(e.target.value)}
                        value={transitnumber}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>IBAN:</Form.Label>
                      <Form.Control
                        maxLength={34}
                        placeholder="e.g., GR123456789012345678901234567"
                        onChange={(e) => setIban(e.target.value.toUpperCase())}
                        value={iban}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Status Information */}
                <h6 className="mb-3 mt-4">Status Information</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        id="active-switch"
                        label="Account is active"
                        checked={active}
                        onChange={(e) => setActive(e.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto">
            {!isFormValid ? (
              <ul
                className="mr-auto"
                style={{ margin: 0, padding: 0, color: "red" }}
              >
                {!isClientValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Client is required.
                  </li>
                )}
                {!isBankValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Bank is required.
                  </li>
                )}
                {!isTransitnumberValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Branch Number is required and must be exactly 5 digits.
                  </li>
                )}
                {!isAccountnumberValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Account Number is required (2–20 chars).
                  </li>
                )}
              </ul>
            ) : (
              <ul
                className="mr-auto"
                style={{ margin: 0, padding: 0, color: "green" }}
              >
                <li>
                  <AiOutlineCheckCircle
                    style={{ fontSize: 18, marginRight: 6 }}
                  />
                  Validated
                </li>
              </ul>
            )}
          </small>

          <Button color="red" onClick={handleClose}>
            Close
          </Button>
          <Button
            color="green"
            onClick={createNewBankClientAccount}
            disabled={!isFormValid}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </GuardedModal>

      <AddBankModal
        nested
        hideTrigger
        show={addBankOpen}
        onHide={() => setAddBankOpen(false)}
        onBankCreated={async (data) => {
          await loadDropdownData();
          const id = data?.bank_id;
          if (id) setBank_id(id);
          setAddBankOpen(false);
        }}
      />
      <AddClientModal
        nested
        hideTrigger
        show={addClientOpen}
        onHide={() => setAddClientOpen(false)}
        onClientCreated={async (data) => {
          await loadDropdownData();
          const id = data?.client_id;
          if (id) setClient_id(id);
          setAddClientOpen(false);
        }}
      />
    </>
  );
}

export default AddBankClientAccountModal;
